import { useCallback, useContext, useEffect, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import WorkUsers from '@atom/components/common/workUsers/WorkUsers';
import { DELETE_POLICY } from '@atom/graph/policy';
import { GET_USERS } from '@atom/graph/user';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, IconButton, ListTable, Progress, Snackbar } from '@atom/mui';
import { AddIcon, DeleteIcon, EditIcon, SettingsIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  GrantType,
  Policy,
  POLICY_GRANT_IDS_ALL,
  PolicyResource,
} from '@atom/types/policy';
import { UserDetail, UsersConnectionInput } from '@atom/types/user';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  getPolicyActionLabel,
  getResourceOptions,
  SUB_SOURCE_LABEL,
} from '@atom/utilities/policyUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import RoleContext from '../RoleContext';

import CreatePolicyModal from './policyModals/CreatePolicyModal';
import EditPolicyModal from './policyModals/EditPolicyModal';

import classes from './rolePolicies.module.css';

const styles = {
  emptyIcon: {
    fontSize: '5.25rem',
    marginBottom: '1rem',
  },
  cellOverflow: {
    maxHeight: '8rem',
    overflow: 'auto',
  },
};

const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

type SortField = 'resource' | 'createdDate';
type SortDirection = 'asc' | 'desc';

const RolePolicies = () => {
  const client = useApolloClient();

  const preferences = usePreferences();

  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [selectedForEdit, setSelectedForEdit] = useState<Policy>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<string>('');
  const [budgetUsers, setBudgetUsers] = useState<{
    [id: string]: UserDetail[];
  }>({});

  const {
    role,
    refetch,
    loading,
    policiesInput: input,
    setPoliciesInput: setInput,
  } = useContext(RoleContext);

  const {
    policies: { policies, totalCount },
  } = role;
  const resourceOptions = getResourceOptions(preferences);

  const [deletePolicy, { loading: loadingDelete }] = useMutation(DELETE_POLICY);

  const getUsers = async (
    // eslint-disable-next-line @typescript-eslint/no-shadow
    input: UsersConnectionInput,
  ) => {
    try {
      const { data } = await client.query({
        query: GET_USERS,
        variables: {
          input: {
            ...input,
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
          },
        },
      });

      const allUsers = data?.users?.users || [];

      setBudgetUsers(
        policies.reduce((usersDict, policy) => {
          if (policy.resource === PolicyResource.BUDGET) {
            usersDict[policy.id] = allUsers.filter(user =>
              policy.grant.ids.includes(user.id),
            );
          }
          return usersDict;
        }, {}),
      );
    } catch (err) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    }
  };

  useEffect(() => {
    getUsers({
      ids: policies.reduce((ids, policy) => {
        return policy.resource === PolicyResource.BUDGET
          ? [...ids, ...policy.grants.map(grant => grant.id)]
          : ids;
      }, []),
    });
  }, [policies]);

  const getSortDirection = useCallback(
    (field: SortField) => {
      if (input.sortBy !== field) {
        return false;
      }

      return input.sortDirection;
    },
    [input.sortBy, input.sortDirection],
  );

  const handleSortDirectionChange =
    (sortBy: SortField) => (sortDirection: SortDirection) => {
      setInput({ ...input, sortBy, sortDirection });
    };

  const handleDelete = async () => {
    await deletePolicy({ variables: { id: selectedForDelete } });
    setSelectedForDelete('');
    refetch();
  };

  const getIDComponent = (policy: Policy) => {
    if (policy.grant.type === GrantType.USER) {
      return <WorkUsers users={budgetUsers[policy.id]} userGroups={[]} />;
    }

    return policy.grant.ids === POLICY_GRANT_IDS_ALL
      ? 'All'
      : policy.grants.map(resource => resource.name).join(', ');
  };

  const hasPolicies = totalCount > 0;
  const policiesSuffix = totalCount === 1 ? 'policy' : 'policies';

  return (
    <>
      <div className={classes.headerContainer}>
        <div>{`${totalCount} ${policiesSuffix}`}</div>
        {hasRolePermissions(ROLE_SETS.ORG_ADMIN) && (
          <IconButton
            data-cy="roleDetailAddPolicyButton"
            onClick={() => setCreateModalOpen(true)}
          >
            <Icon icon={AddIcon} />
          </IconButton>
        )}
      </div>
      <div className={classes.content}>
        {loading ? (
          <Progress style={{ flex: 1 }} />
        ) : (
          <ListTable offsetTop="310px" fullHeight={hasPolicies}>
            <TableHead>
              <TableRow header>
                <TableCell
                  variant="head"
                  sortDirection={getSortDirection('resource')}
                  onSortChange={handleSortDirectionChange('resource')}
                >
                  Source
                </TableCell>
                <TableCell variant="head">Sub Source</TableCell>
                <TableCell variant="head">ID</TableCell>
                <TableCell variant="head">Operation</TableCell>
                <TableCell
                  variant="head"
                  sortDirection={getSortDirection('createdDate')}
                  onSortChange={handleSortDirectionChange('createdDate')}
                >
                  Created On
                </TableCell>
                <TableCell variant="head" />
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map(policy => {
                const option = resourceOptions[policy.resource];

                if (!option) {
                  return null;
                }

                return (
                  <TableRow
                    key={[
                      policy.subjectId,
                      policy.resource,
                      policy.action,
                      policy.grant.type,
                    ].join('-')}
                  >
                    <TableCell>
                      <span style={{ marginRight: '1rem' }}>{option.icon}</span>
                      {option.label}
                    </TableCell>
                    <TableCell>
                      {option.grantTypes.length > 1 ||
                      policy.resource === PolicyResource.TIME_SHEET
                        ? SUB_SOURCE_LABEL[policy.grant.type]
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div style={styles.cellOverflow}>
                        {getIDComponent(policy)}
                      </div>
                    </TableCell>
                    <TableCell>{getPolicyActionLabel(policy.action)}</TableCell>
                    <TableCell>{setDisplayDate(policy.updatedDate)}</TableCell>
                    <TableCell width="96px">
                      {hasRolePermissions(ROLE_SETS.ORG_ADMIN) ? (
                        <>
                          <IconButton
                            data-cy={`roleDetailEditPolicyButton-${policy.id}`}
                            onClick={() => setSelectedForEdit(policy)}
                          >
                            <Icon icon={EditIcon} />
                          </IconButton>
                          <IconButton
                            data-cy={`roleDetailDeletePolicyButton-${policy.id}`}
                            onClick={() => setSelectedForDelete(policy.id)}
                          >
                            <Icon icon={DeleteIcon} />
                          </IconButton>
                        </>
                      ) : (
                        <div style={{ height: '48px' }} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            {hasPolicies && (
              <TableFooter width="70%">
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[15, 25, 50]}
                    count={totalCount}
                    rowsPerPage={input.limit}
                    page={input.page}
                    onPageChange={page => setInput({ ...input, page })}
                    onRowsPerPageChange={event =>
                      setInput({
                        ...input,
                        limit: +event.target.value,
                        page: 1,
                      })
                    }
                  />
                </TableRow>
              </TableFooter>
            )}
          </ListTable>
        )}
        {!hasPolicies && !loading && (
          <div className={classes.emptyContainer}>
            <Icon
              style={styles.emptyIcon}
              color={colors.neutral.silver}
              icon={SettingsIcon}
            />
            <div>There are no policies.</div>
          </div>
        )}
      </div>
      <CreatePolicyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <EditPolicyModal
        open={!!selectedForEdit}
        onClose={() => setSelectedForEdit(null)}
        policy={selectedForEdit}
      />
      <DeleteModal
        title="Delete Policy?"
        open={!!selectedForDelete}
        onCancel={() => setSelectedForDelete('')}
        onConfirm={handleDelete}
        loading={loadingDelete}
        content="Deleting this policy will remove it from all users and groups in this role. Are you sure?"
      />
    </>
  );
};

export default RolePolicies;
